import {
  CONTENT_TEST_ARTIFACT_KEY_PREFIX,
  ContentTestWidgetKey,
  CopyGenerationWidgetKey,
  FOCUS_GROUP_ARTIFACT_KEY_PREFIX,
  FocusGroupWidgetKey,
  IdeationWidgetKey,
  SAVED_COPY_ARTIFACT_KEY_PREFIX,
  SAVED_IDEATION_CAMPAIGN_ARTIFACT_KEY_PREFIX,
  SAVED_IDEATION_IDEA_ARTIFACT_KEY_PREFIX,
  SAVED_STRATEGIC_TERRITORIES_ARTIFACT_KEY_PREFIX,
  StrategicTerritoriesWidgetKey,
} from '@inference.engine/widgets/web';

/**
 * Saved artifacts carry a `${prefix}${id}` key rather than the canonical widget
 * key, so an exact-match thumbnail lookup misses them and the Library falls back
 * to the palette icon. Each saved kind freezes a self-contained snapshot into
 * `artifact.data` at save time, so the thumbnail renders straight from that
 * `data` once we map the prefix back to the canonical widget key. Keep entries
 * in sync with the `*_ARTIFACT_KEY_PREFIX` constants.
 *
 * Kept in its own module (importing only widget key/prefix string constants,
 * never the widget component graph) so the normalization is unit-testable
 * without pulling in `agents.config`'s heavy, mock-required import graph.
 */
const PREFIX_TO_WIDGET_KEY: ReadonlyArray<readonly [string, string]> = [
  [SAVED_STRATEGIC_TERRITORIES_ARTIFACT_KEY_PREFIX, StrategicTerritoriesWidgetKey],
  [SAVED_COPY_ARTIFACT_KEY_PREFIX, CopyGenerationWidgetKey],
  // Both ideation saves (whole campaign + single-idea snapshot) store V2-shaped
  // data, so both resolve to the V2 widget key for thumbnail rendering.
  [SAVED_IDEATION_CAMPAIGN_ARTIFACT_KEY_PREFIX, IdeationWidgetKey],
  [SAVED_IDEATION_IDEA_ARTIFACT_KEY_PREFIX, IdeationWidgetKey],
  [CONTENT_TEST_ARTIFACT_KEY_PREFIX, ContentTestWidgetKey],
  [FOCUS_GROUP_ARTIFACT_KEY_PREFIX, FocusGroupWidgetKey],
];

/**
 * Normalizes an artifact key (`${prefix}${id}`) to the canonical widget key it
 * maps to; returns the key unchanged when it carries no known prefix.
 */
export const normalizeSavedWidgetKey = (widgetKey: string): string => {
  for (const [prefix, canonicalKey] of PREFIX_TO_WIDGET_KEY) {
    if (widgetKey.startsWith(prefix)) return canonicalKey;
  }
  return widgetKey;
};
